import type { ReactNode } from 'react';
import { Card, Space, Table, type TableProps } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { useBreakpoint } from '@/shared/lib/hooks';
import { EmptyState } from './EmptyState';

export interface ResponsiveTableProps<T extends object> extends TableProps<T> {
  /** Custom mobile card body — omit to fall back to a label/value list built from `columns`. */
  renderCard?: (record: T, index: number) => ReactNode;
}

/** `Table` at ≥768px, a card list below it (docs/PLAN.md §6) — every admin list uses this instead
 *  of antd's `Table` directly so it never forces horizontal scroll on a phone. */
export function ResponsiveTable<T extends object>({
  renderCard,
  columns,
  dataSource,
  rowKey,
  ...rest
}: ResponsiveTableProps<T>) {
  const { isMobile } = useBreakpoint();

  if (!isMobile) {
    return <Table<T> columns={columns} dataSource={dataSource} rowKey={rowKey} {...rest} />;
  }

  const records = dataSource ?? [];
  if (records.length === 0) return <EmptyState />;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      {records.map((record, index) => (
        <Card key={resolveKey(record, rowKey, index)} size="small">
          {renderCard ? renderCard(record, index) : defaultCardBody(record, columns)}
        </Card>
      ))}
    </Space>
  );
}

function resolveKey<T extends object>(
  record: T,
  rowKey: ResponsiveTableProps<T>['rowKey'],
  index: number,
): string | number {
  if (typeof rowKey === 'function') return rowKey(record, index) as string | number;
  if (typeof rowKey === 'string' && rowKey in record) {
    return (record as Record<string, unknown>)[rowKey] as string | number;
  }
  return index;
}

function defaultCardBody<T extends object>(record: T, columns: ColumnType<T>[] | undefined) {
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {(columns ?? []).map((column, columnIndex) => {
        const dataIndex = column.dataIndex as keyof T | undefined;
        const rawValue = dataIndex ? record[dataIndex] : undefined;
        const rendered = column.render
          ? column.render(rawValue, record, columnIndex)
          : (rawValue as ReactNode);
        // `render` may return a `{ children, props }` cell-merge descriptor instead of a plain
        // node (used for row/col spanning in the real Table) — unwrap it for the card view.
        const value =
          rendered !== null && typeof rendered === 'object' && 'children' in rendered
            ? (rendered as { children: ReactNode }).children
            : (rendered as ReactNode);
        if (value === undefined || value === null || value === '') return null;
        return (
          <div
            key={column.key ?? (dataIndex ? String(dataIndex) : columnIndex)}
            style={{ display: 'flex', gap: 8 }}
          >
            <span style={{ color: 'var(--color-text-secondary, #8c8c8c)', minWidth: 100 }}>
              {column.title as ReactNode}
            </span>
            <span>{value}</span>
          </div>
        );
      })}
    </Space>
  );
}
